import { useState } from 'react';
import { api } from '../api/client';
import type { CheckRow } from '../api/types';
import { ago, duration } from '../lib/format';
import { can, useMe } from '../lib/me';
import { useLoad } from '../lib/useLoad';
import { LoadIssue } from '../components/LoadIssue';

/**
 * Whole days a check has been in shadow, or null when that is not known.
 *
 * Null rather than a number for a row with no `shadowSince` -- one older than
 * the column. `new Date(null)` is 1970, and "shadow · 20,365d" on that row
 * would read as a check forgotten for half a century rather than one whose
 * start was never written down.
 */
export const shadowDays = (shadowSince: string | null, now = Date.now()): number | null => {
  if (!shadowSince) return null;
  const since = new Date(shadowSince).getTime();
  if (Number.isNaN(since)) return null;
  return Math.max(0, Math.floor((now - since) / 86_400_000));
};

/** The chip's words: how long, when that is known. */
export const shadowLabel = (shadowSince: string | null, now = Date.now()): string => {
  const days = shadowDays(shadowSince, now);
  return days === null ? 'shadow' : `shadow · ${days}d`;
};

/**
 * What is actually being watched -- and, more usefully, what is NOT.
 *
 * This screen exists because "no open incidents" and "everything is fine" are
 * different statements, and only this one can tell them apart. A check that has
 * gone blind (its column moved, its grant was revoked) reports `unknown`, opens
 * no incident, and would be invisible on the Now board forever.
 *
 * It is also where a check leaves shadow. A check in shadow records and opens
 * incidents and tells nobody; the chip counts the days since it went in, and
 * two weeks of watching what it fires on is the evidence for graduating it.
 * Either button changes the NEXT incident only -- one already open keeps what
 * it opened with -- and "Return to shadow" asks twice, because it is a mute
 * with no end date.
 */
export function Checks() {
  const canShadow = can(useMe(), 'checks.shadow');
  const [busy, setBusy] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [note, setNote] = useState<{ tone: 'ok' | 'danger'; text: string } | null>(null);
  const { data, error, loading, updatedAt, reload } = useLoad(
    () => api.get<{ checks: CheckRow[] }>('/checks'),
    [],
    30_000,
  );

  const setShadow = async (check: CheckRow, shadow: boolean) => {
    setBusy(check.id);
    setConfirming(null);
    setNote(null);
    try {
      await api.post(`/checks/${encodeURIComponent(check.id)}/shadow`, { shadow });
      setNote({
        tone: 'ok',
        text: shadow
          ? `${check.id} is back in shadow: its new incidents are recorded and not notified, and its count starts again.`
          : `${check.id} has graduated: its next incident notifies. Incidents already open keep what they opened with.`,
      });
      await reload();
    } catch (caught) {
      setNote({ tone: 'danger', text: `Could not change ${check.id}: ${(caught as Error).message}` });
    } finally {
      setBusy(null);
    }
  };

  if (loading) return <div className="wt-empty">Loading…</div>;
  if (!data) return <LoadIssue error={error} updatedAt={null} what="checks" onRetry={() => void reload()} />;

  const checks = data?.checks ?? [];
  const blind = checks.filter((c) => c.consecutiveUnknown >= 3);
  const stale = checks.filter(
    (c) => c.enabled && !c.retiredAt && c.lastRunAt && Date.now() - new Date(c.lastRunAt).getTime() > c.intervalMs * 3,
  );

  return (
    <>
      <div className="wt-head">
        <h1>Checks</h1>
        <span className="sub">
          {checks.filter((c) => !c.retiredAt).length} live · {checks.filter((c) => c.shadow).length} in shadow
        </span>
      </div>
      <LoadIssue error={error} updatedAt={updatedAt} what="checks" onRetry={() => void reload()} />

      {note && <div className={`wt-banner${note.tone === 'danger' ? ' wt-banner-danger' : ''}`}>{note.text}</div>}
      {blind.length > 0 && (
        <div className="wt-banner wt-banner-danger">
          {blind.length} check(s) cannot run — a column moved or a grant was revoked. These raise no
          incident of their own, so nothing else on this console would show it.
        </div>
      )}
      {stale.length > 0 && (
        <div className="wt-banner wt-banner-warn">
          {stale.length} check(s) have not run in over three intervals.
        </div>
      )}

      <div className="wt-table-wrap">
        <table className="wt-table wt-checks wt-stack">
          <thead>
            <tr>
              <th>Check</th>
              <th>System</th>
              <th>Last</th>
              <th>Every</th>
              <th>Ran</th>
              <th>State</th>
            </tr>
          </thead>
          <tbody>
            {checks.map((check) => (
              <tr key={check.id} style={check.retiredAt ? { opacity: 0.5 } : undefined}>
                <td className="wt-stack-wide">
                  <div>
                    <span className={`wt-dot wt-st-${check.lastStatus ?? 'null'}`} />
                    {check.title}
                  </div>
                  <div className="wt-mono">
                    {check.ref ? `${check.ref} · ` : ''}
                    {check.id}
                  </div>
                  {/* What the latest run said. For an unknown or a skip this is the
                      reason, which is the first thing anyone debugging it wants. */}
                  {check.lastSummary && (
                    <div
                      className={`wt-check-summary${check.lastStatus === 'unknown' ? ' blind' : ''}`}
                      title={check.lastSummary}
                    >
                      {check.lastSummary}
                    </div>
                  )}
                </td>
                <td className="wt-mono" data-label="System">
                  {check.system}
                </td>
                <td className="wt-mono" data-label="Last">
                  {check.lastStatus ?? 'never run'}
                </td>
                <td className="wt-mono" data-label="Every">
                  {duration(check.intervalMs)}
                </td>
                <td className="wt-mono" data-label="Ran">
                  {ago(check.lastRunAt)}
                </td>
                <td className="wt-stack-wide wt-check-state-cell">
                  <div className="wt-check-state">
                    {check.retiredAt && <span className="wt-chip">retired</span>}
                    {!check.enabled && <span className="wt-chip wt-chip-flap">disabled</span>}
                    {check.shadow && (
                      <span
                        className="wt-chip wt-chip-shadow"
                        title="Its incidents are recorded and not notified. Days since it went into shadow."
                      >
                        {shadowLabel(check.shadowSince)}
                      </span>
                    )}
                    {check.severityOverride && check.severityOverride !== check.severity && (
                      <span className="wt-chip" title={`Written as ${check.severity}; opens incidents as this`}>
                        as {check.severityOverride}
                      </span>
                    )}
                    {check.consecutiveUnknown >= 3 && (
                      <span className="wt-chip wt-chip-flap">blind ×{check.consecutiveUnknown}</span>
                    )}
                  </div>
                  {canShadow && !check.retiredAt && (
                    <div className="wt-check-actions">
                      {check.shadow ? (
                        <button
                          type="button"
                          className="btn btn-secondary"
                          disabled={busy === check.id}
                          onClick={() => void setShadow(check, false)}
                        >
                          Graduate
                        </button>
                      ) : confirming === check.id ? (
                        <>
                          <button
                            type="button"
                            className="btn btn-danger"
                            disabled={busy === check.id}
                            onClick={() => void setShadow(check, true)}
                          >
                            Stop notifying?
                          </button>
                          <button type="button" className="btn btn-ghost" onClick={() => setConfirming(null)}>
                            Keep
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-ghost wt-check-return"
                          disabled={busy === check.id}
                          onClick={() => setConfirming(check.id)}
                        >
                          Return to shadow
                        </button>
                      )}
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
